import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import {
  Col,
  Container,
  Row,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "components/Common/Breadcrumb";
import Spinners from "components/Common/Spinner";
import toast from "react-hot-toast";
import moment from "moment";

import {
  getAllPayouts,
  getAllOrganizer,
} from "api";
import { handleError } from "utils/errorHandler";
import { formatCurrency } from "utils/revenueCalculator";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "paid", label: "Paid" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const PAGE_SIZE = 8;

const OrganizerPayout = () => {
  document.title = "Payouts | EventoX";

  const navigate = useNavigate();

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [organizers, setOrganizers] = useState([]);
  const [organizerFilter, setOrganizerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Date range state (default: last 30 days)
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  const loadPayouts = async () => {
    setLoading(true);
    try {
      const data = await getAllPayouts();
      const arr = Array.isArray(data) ? data : data?.data || [];
      setPayouts(arr);
      return arr;
    } catch (err) {
      handleError(err);
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizers = async () => {
    try {
      const data = await getAllOrganizer();
      setOrganizers(Array.isArray(data) ? data : data?.data || []);
    } catch (_) {}
  };

  useEffect(() => {
    loadPayouts();
    loadOrganizers();
  }, []);

  const filteredPayouts = useMemo(() => {
    if (!Array.isArray(payouts)) return [];
    return payouts.filter((p) => {
      const org = p.Organizer || p.organizer;
      const ev = p.Event || p.event;
      const matchesStatus =
        statusFilter === "all" ||
        String(p.status || "").toUpperCase() === statusFilter.toUpperCase();
      const matchesOrganizer =
        organizerFilter === "all" || String(p.organizerId || org?.userId) === String(organizerFilter);
      const matchesSearch =
        !search ||
        (org?.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (ev?.title || ev?.name || "").toLowerCase().includes(search.toLowerCase());
      const matchesDate =
        !p.createdAt ||
        (moment(p.createdAt).isSameOrAfter(dateFrom, "day") &&
          moment(p.createdAt).isSameOrBefore(dateTo, "day"));
      return matchesStatus && matchesOrganizer && matchesSearch && matchesDate;
    });
  }, [payouts, statusFilter, organizerFilter, search, dateFrom, dateTo]);

  const tabCounts = useMemo(() => {
    const counts = { all: payouts.length };
    STATUS_TABS.slice(1).forEach((t) => {
      counts[t.value] = payouts.filter(
        (p) => String(p.status || "").toUpperCase() === t.value.toUpperCase()
      ).length;
    });
    return counts;
  }, [payouts]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredPayouts.length / PAGE_SIZE));
  const pagedPayouts = filteredPayouts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Reset to page 1 on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, organizerFilter, search, dateFrom, dateTo]);

  const formatDate = (d) =>
    d && moment(d).isValid() ? moment(d).format("D MMM YYYY, hh:mm A") : "—";

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={classnames("payout-page-btn", { active: currentPage === i })}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="payout-pagination">
        <span className="payout-showing">
          Showing {filteredPayouts.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} to{" "}
          {Math.min(currentPage * PAGE_SIZE, filteredPayouts.length)} of {filteredPayouts.length} results
        </span>
        <div className="payout-pages">
          <button
            className="payout-page-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          {pages}
          <button
            className="payout-page-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="page-content">
      <style>{`
        /* ── Payout Page Styles ── */
        .payout-wrap { background: #f8f9fc; min-height: 100vh; }

        /* Tab bar */
        .payout-tabs {
          display: flex;
          gap: 0;
          border-bottom: 2px solid #e9ecef;
          margin-bottom: 20px;
          background: #fff;
          padding: 0 4px;
        }
        .payout-tab {
          padding: 14px 20px;
          font-size: 14px;
          font-weight: 500;
          color: #6c757d;
          cursor: pointer;
          border: none;
          background: transparent;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .payout-tab:hover { color: #3b5bdb; }
        .payout-tab.active {
          color: #3b5bdb;
          border-bottom-color: #3b5bdb;
          font-weight: 600;
        }
        .payout-tab .tab-count {
          font-size: 12px;
          font-weight: 500;
          color: inherit;
          opacity: 0.75;
        }

        /* Filter bar */
        .payout-filter-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding: 16px;
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          margin-bottom: 4px;
        }
        .payout-search-wrap {
          position: relative;
          flex: 1;
          min-width: 200px;
        }
        .payout-search-wrap i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #adb5bd;
          font-size: 15px;
        }
        .payout-search-wrap input {
          padding-left: 32px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          height: 38px;
          font-size: 13.5px;
          width: 100%;
          outline: none;
          color: #495057;
        }
        .payout-search-wrap input:focus { border-color: #3b5bdb; box-shadow: 0 0 0 2px rgba(59,91,219,0.1); }

        .payout-filter-group {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 150px;
        }
        .payout-filter-group label {
          font-size: 11px;
          font-weight: 600;
          color: #adb5bd;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0;
        }
        .payout-filter-group select,
        .payout-filter-group input[type="date"] {
          border: 1px solid #dee2e6;
          border-radius: 6px;
          height: 36px;
          font-size: 13.5px;
          padding: 0 8px;
          color: #495057;
          background: #fff;
          outline: none;
          width: 100%;
        }
        .payout-filter-group select:focus,
        .payout-filter-group input[type="date"]:focus {
          border-color: #3b5bdb;
          box-shadow: 0 0 0 2px rgba(59,91,219,0.1);
        }

        .payout-date-range {
          display: flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          padding: 4px 10px;
          background: #fff;
          height: 36px;
        }
        .payout-date-range input[type="date"] {
          border: none;
          font-size: 13px;
          color: #495057;
          outline: none;
          width: 130px;
          background: transparent;
          padding: 0;
          height: auto;
        }
        .payout-date-range span { color: #adb5bd; font-size: 13px; }
        .payout-date-range i { color: #adb5bd; font-size: 15px; }

        .payout-filter-actions { margin-left: auto; display: flex; gap: 8px; }
        .payout-btn-filter, .payout-btn-export {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13.5px;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid #dee2e6;
          background: #fff;
          color: #495057;
          transition: background 0.15s, border-color 0.15s;
        }
        .payout-btn-filter:hover { background: #f1f3f5; border-color: #adb5bd; }
        .payout-btn-export { background: #fff; }
        .payout-btn-export:hover { background: #f1f3f5; }

        /* Table */
        .payout-table-card {
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          overflow: hidden;
        }
        .payout-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
        }
        .payout-table thead th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 12.5px;
          color: #6c757d;
          background: #f8f9fc;
          border-bottom: 1px solid #e9ecef;
          white-space: nowrap;
        }
        .payout-table tbody tr {
          border-bottom: 1px solid #f1f3f5;
          transition: background 0.1s;
        }
        .payout-table tbody tr:last-child { border-bottom: none; }
        .payout-table tbody tr:hover { background: #f8f9ff; cursor: pointer; }
        .payout-table td {
          padding: 13px 16px;
          color: #212529;
          vertical-align: middle;
        }
        .payout-id { font-weight: 600; color: #3b5bdb; }
        .payout-org { font-weight: 500; }
        .payout-amount { font-weight: 600; }

        /* Badges */
        .payout-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 50px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }
        .pb-verified   { background: #d3f9d8; color: #2f9e44; }
        .pb-pending    { background: #fff3cd; color: #e67700; }
        .pb-processing { background: #dbe4ff; color: #3b5bdb; }
        .pb-paid       { background: #d3f9d8; color: #2f9e44; }
        .pb-rejected   { background: #ffe3e3; color: #c92a2a; }
        .pb-cancelled  { background: #f1f3f5; color: #868e96; }
        .pb-approved   { background: #d0ebff; color: #1971c2; }

        /* Action buttons */
        .payout-action-cell { display: flex; align-items: center; gap: 8px; }
        .payout-view-btn {
          padding: 5px 14px;
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #3b5bdb;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
        }
        .payout-view-btn:hover { background: #eef1ff; border-color: #3b5bdb; }

        /* Pagination */
        .payout-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-top: 1px solid #f1f3f5;
          font-size: 13px;
          color: #6c757d;
        }
        .payout-pages { display: flex; gap: 4px; align-items: center; }
        .payout-page-btn {
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          background: #fff;
          color: #495057;
          font-size: 13px;
          cursor: pointer;
          padding: 0 6px;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .payout-page-btn:hover:not(:disabled):not(.active) { background: #f1f3f5; }
        .payout-page-btn.active { background: #3b5bdb; border-color: #3b5bdb; color: #fff; font-weight: 600; }
        .payout-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .empty-state { text-align: center; padding: 48px 16px; color: #adb5bd; }
        .empty-state i { font-size: 36px; margin-bottom: 12px; display: block; }
      `}</style>

      <Container fluid className="payout-wrap">
        <Breadcrumbs title="Admin" breadcrumbItem="Payout Requests" />

        {/* Status tabs */}
        <div className="payout-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              className={classnames("payout-tab", { active: statusFilter === tab.value })}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}{" "}
              <span className="tab-count">({tabCounts[tab.value] ?? 0})</span>
            </button>
          ))}
        </div>

        <Row>
          <Col lg={12}>
            {/* Filter bar */}
            <div className="payout-filter-bar mb-3">
              <div className="payout-search-wrap">
                <i className="mdi mdi-magnify" />
                <input
                  type="text"
                  placeholder="Search organizer or event..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="payout-filter-group" style={{ minWidth: 160 }}>
                <label>Organizer</label>
                <select
                  value={organizerFilter}
                  onChange={(e) => setOrganizerFilter(e.target.value)}
                >
                  <option value="all">All Organizers</option>
                  {organizers.map((org) => {
                    const orgId = org.organizerId || org.userId || org.id;
                    return (
                      <option key={orgId} value={orgId}>
                        {org.name || org.username || `Organizer ${orgId}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="payout-filter-group" style={{ minWidth: 140 }}>
                <label>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Statuses</option>
                  {STATUS_TABS.slice(1).map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="payout-filter-group" style={{ minWidth: 280 }}>
                <label>Date Range</label>
                <div className="payout-date-range">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                  <span>–</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                  <i className="mdi mdi-calendar-outline" />
                </div>
              </div>

              <div className="payout-filter-actions">
                <button className="payout-btn-filter">
                  <i className="mdi mdi-filter-outline" /> Filters
                </button>
                <button className="payout-btn-export">
                  <i className="mdi mdi-download-outline" /> Export
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="payout-table-card">
              {loading ? (
                <div className="p-4">
                  <Spinners setLoading={setLoading} />
                </div>
              ) : filteredPayouts.length === 0 ? (
                <div className="empty-state">
                  <i className="mdi mdi-cash-remove" />
                  No payout requests found.
                </div>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table className="payout-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Organizer</th>
                          <th>Event</th>
                          <th>Amount</th>
                          <th>Bank Status</th>
                          <th>Status</th>
                          <th>Requested On</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedPayouts.map((item) => {
                          const id = item.payoutId;
                          const org = item.Organizer || item.organizer;
                          const ev = item.Event || item.event;
                          const status = (item.status || "PENDING").toUpperCase();
                          const snap = item.bankDetails || item.BankDetail;
                          let bd = snap;
                          if (!bd && org) {
                            if (org.BankDetail) bd = org.BankDetail;
                            else if (org.bankDetails) {
                              bd = Array.isArray(org.bankDetails)
                                ? org.bankDetails.find((b) => b.isDefault) || org.bankDetails[0]
                                : org.bankDetails;
                            }
                          }
                          const bvs = (bd?.verificationStatus || item.bankVerificationStatus || "PENDING").toUpperCase();
                          const amount = item.amount || item.requestedAmount || 0;
                          const currency = item.currency || "USD";
                          return (
                            <tr
                              key={id}
                              onClick={() => navigate(`/organizer-payout/${id}`)}
                            >
                              <td className="payout-id">
                                #PAY-{id}
                              </td>
                              <td className="payout-org">
                                {org?.name || item.organizerId || "—"}
                              </td>
                              <td>
                                {ev?.title || item.eventId || "—"}
                              </td>

                              <td className="payout-amount">
                                {formatCurrency(amount, currency)}
                              </td>
                              <td>
                                <span
                                  className={`payout-badge ${
                                    bvs === "VERIFIED"
                                      ? "pb-verified"
                                      : bvs === "REJECTED"
                                      ? "pb-rejected"
                                      : "pb-pending"
                                  }`}
                                >
                                  {bvs.charAt(0) + bvs.slice(1).toLowerCase()}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`payout-badge ${
                                    status === "PAID"
                                      ? "pb-paid"
                                      : status === "PROCESSING"
                                      ? "pb-processing"
                                      : status === "REJECTED"
                                      ? "pb-rejected"
                                      : status === "CANCELLED"
                                      ? "pb-cancelled"
                                      : status === "APPROVED"
                                      ? "pb-approved"
                                      : "pb-pending"
                                  }`}
                                >
                                  {status.charAt(0) + status.slice(1).toLowerCase()}
                                </span>
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatDate(item.createdAt)}
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="payout-action-cell">
                                  <button
                                    className="payout-view-btn"
                                    onClick={() => navigate(`/organizer-payout/${id}`)}
                                  >
                                    View
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination()}
                </>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withRouter(OrganizerPayout);